// src/app/guards/admin.guard.ts
import { Injectable } from '@angular/core';
import {
    CanActivate,
    CanMatch,
    Router,
    UrlTree,
    ActivatedRouteSnapshot,
    RouterStateSnapshot,
    Route,
    UrlSegment
} from '@angular/router';
import { EmployeeService } from '@/app/domains/auth/employee/data-access/employee.service';

@Injectable({ providedIn: 'root' })
export class AdminGuard implements CanActivate, CanMatch {
    constructor(private emp: EmployeeService, private router: Router) {}

    canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
        return this.check(state.url);
    }

    canMatch(_route: Route, _segments: UrlSegment[]): Promise<boolean | UrlTree> {
        // En canMatch no tenemos state.url directo, pero igual podemos inferir algo:
        const url = '/' + (_segments || []).map(s => s.path).join('/');
        return this.check(url || '/');
    }

    private async check(targetUrl: string): Promise<boolean | UrlTree> {
        // Define aquí tu ruta de "acceso denegado" (ADMIN)
        // Ideal: una pantalla simple "No autorizado" + botón "Cerrar sesión" o "Volver".
        const forbiddenUrl = '/not-found'; // <-- AJUSTA ESTA RUTA A TU APP

        try {
            // Login sin terminar (no eligió modo/sucursal): volver al login,
            // que descarta la sesión y pide reingresar.
            if (await this.emp.isLoginPending()) return this.router.parseUrl('/login');

            // 1) Debe existir backend_token guardado
            const backendToken = await this.emp.getBackendToken();
            if (!backendToken) return this.router.parseUrl('/login');

            const valid = await this.emp.backendTokenIsValid();
            if (!valid) return this.router.parseUrl('/login');

            // obtenemos employee y roles desde el servicio
            const employee: any = await this.emp.getEmployee();

            // prioridad: roles guardados explícitamente en EmployeeService (si existen)
            let roles: any[] = [];
            if ((this.emp as any).getRoles) {
                roles = (await (this.emp as any).getRoles()) || [];
            }

            // fallback: si no hay getRoles o viene vacío, usamos employee.roles si existe
            if (!Array.isArray(roles) || roles.length === 0) {
                roles = Array.isArray(employee?.roles) ? employee.roles : [];
            }

            const isSysAdmin = Array.isArray(roles)
                ? roles.some((r: any) => r?.code === 'SYS_ADMIN')
                : false;

            // 2) Si no es admin, NO lo mandes al menú de colaborador
            if (!employee || !isSysAdmin) {
                // Si quieres, aquí podrías mandar a un "home" neutro, o al login.
                // Pero forbidden suele ser lo más correcto para mantener la sesión sin mezclar contextos.
                return this.router.parseUrl(forbiddenUrl);
            }

            // 3) Si es admin, permitir
            return true;
        } catch {
            return this.router.parseUrl('/login');
        }
    }
}
